/*
 AI 촉각 변환 한 번 = 크레딧 요청 하나 + 외부 AI 호출 하나
 (user-app v2 비용 기반 AI 크레딧, 연동 매뉴얼 2026-10-29 개정 2 · 계약 COST_1).

 순서는 (잔액·견적) → 요청 승인 → 호출 시작 승인 → AI 한 번 → 실제 사용량 정산이다.
 - 승인·시작은 잔액을 다시 확인할 뿐 차감·예약하지 않는다. 차감은 정산 한 번이고, 금액은 서버가 모델 단가·환율로 계산한다.
 - 정산에는 dot-ai 응답의 tokens.input·tokens.output 원본만 보낸다. tokens.total 은 참고값이라 보내지 않고,
   DotAI 에는 없는 thoughts·cache 도 넣지 않는다(매뉴얼 10 「외부 사용량 필드」).
 - 응답에서 사용량을 못 읽으면(AI 실패·「취소」·시간 초과) 0 으로 정산하지 않고 "사용량 미확인"(unknown)으로 남긴다.
   미확인은 차감되지 않는다. 사용량을 받았으면 그림을 못 쓰게 되었어도 그 사용량으로 정산한다(매뉴얼 12).
   (예전 토큰 1:1 방식에서는 실패·취소를 0 으로 정산했다. 새 계약은 0 추정을 금지한다.)

 **시각은 서버가 준 startedAt 에 시작 응답을 받은 뒤 로컬에서 잰 경과 시간을 더해 만든다.**
 서버는 AI 시작 시각이 startedAt−1초 이상·validUntil 미만이어야 하고, 완료 시각이 시작 시각 이상·정산 서버의 현재
 시각+1초 이하여야 받는다. 내 PC 시계를 그대로 쓰면 서버보다 1초 넘게 빠르거나 느릴 때 거절된다. 시작 응답을 받은
 뒤부터 잰 경과는 서버 쪽 경과보다 항상 짧으므로 조건을 다 지킨다.
 (매뉴얼은 시각을 백엔드가 기록하라고 하지만 닷 캔버스에는 AI 를 대신 부르는 백엔드가 없다. 그래서 브라우저 시계는
  쓰지 않고 서버 시각에 경과만 더한다.)

 재시도는 같은 값으로 보낸다. 승인은 같은 requestKey·같은 계획이면 같은 응답이 오고, 정산 재시도에서 시각이나
 사용량을 바꾸면 409 COMPLETION_CONFLICT 다. 끝내 못 보낸 정산·미확인 기록은 저장해 두었다가 다음에 열 때 보낸다.
 */

export const CREDIT_SOURCE_APP = "CANVAS"
export const CREDIT_CONTRACT = "COST_1"

/*
 user-app 연산 설정(seed 11_seed_cost_credit_settings.sql). 시작 승인의 model 은 연산 설정의 EXPECTED_MODEL 과 정확히
 같아야 한다 — 다르면 409 MODEL_MISMATCH(2026-11-02 dev-saas 실측). dot-ai v3 응답에는 모델 이름이 없어 여기 둔다.
 */
export const AI_IMAGE_OPERATIONS = Object.freeze({
    image: Object.freeze({operation: "dotai.image2dots", model: "flash-3.8"}),
    prompt: Object.freeze({operation: "dotai.text2dots", model: "flash-3.8"}),
})

/** 이미지 창의 입구(업로드한 그림 / 설명 글) → 크레딧 연산 */
export function aiImageOperation(entryPath) {
    return entryPath === "prompt" ? AI_IMAGE_OPERATIONS.prompt : AI_IMAGE_OPERATIONS.image
}

const USER_NO_PATTERN = /^[A-Za-z0-9_-]{1,16}$/
const REQUEST_KEY_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/
const PENDING_KEY = "dotCanvas.aiCreditPending.v2"
// 옛 /credits/jobs 정산을 담던 자리. 그 경로는 서버에서 없어져(404) 보낼 곳이 없으므로 보이면 지운다.
const LEGACY_PENDING_KEY = "dotCanvas.aiCreditPending"
const MAX_PENDING = 50
const RETRY_DELAYS = [400, 1200]
// 매뉴얼: 토큰은 정수, 닷북 어댑터는 필드별 999,999,999,999 까지 받는다(JS 안전 정수 범위 안).
const MAX_TOKENS = 999999999999

export class CreditError extends Error {
    constructor(code, {status = 0} = {}) {
        super(code)
        this.name = "CreditError"
        this.code = code
        this.status = status
    }
}

/**
 크레딧 API 경로에 넣을 수 있는 사용자 번호인지.
 로그인하지 않으면 dot-framework 가 "GUEST" 를 돌려준다. 이것으로 부르면 게이트웨이 환경에서 401 →
 토큰 갱신 실패 → 로그인 화면으로 튕기므로 아예 부르지 않는다.
 */
export function isCreditUserNo(userNo) {
    if (typeof userNo !== "string" || !USER_NO_PATTERN.test(userNo)) return false
    const lower = userNo.toLowerCase()
    return lower !== "me" && lower !== "guest"
}

function randomUuid() {
    const cryptoApi = globalThis.crypto
    if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID()
    if (typeof cryptoApi?.getRandomValues === "function") {
        const bytes = cryptoApi.getRandomValues(new Uint8Array(16))
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("")
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** 요청 승인의 멱등 키. 변환 한 번마다 새로 만들고, 같은 승인의 재전송에는 같은 값을 쓴다. */
export function createCreditRequestKey(operation = AI_IMAGE_OPERATIONS.image.operation, randomId = randomUuid()) {
    const requestKey = `canvas:${operation}:${randomId}`
    if (!REQUEST_KEY_PATTERN.test(requestKey)) throw new Error(`invalid credit requestKey: ${requestKey}`)
    return requestKey
}

/** 견적·승인 본문. 촉각 변환 한 번은 외부 AI 호출 한 번이라 quantity 1 이다. */
export function creditPlan(operation, requestKey) {
    return {sourceApp: CREDIT_SOURCE_APP, requestKey, items: [{operation, quantity: 1}]}
}

function tokenCount(value) {
    const number = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value
    return Number.isSafeInteger(number) && number >= 0 && number <= MAX_TOKENS ? number : null
}

/**
 dot-ai 응답의 tokens → 정산 usage {input, output}. 둘 중 하나라도 못 읽으면 null(= 사용량 미확인).
 total 로 메우거나 0 을 채우지 않는다.
 */
export function usageFromTokens(tokens) {
    const input = tokenCount(tokens?.input)
    const output = tokenCount(tokens?.output)
    if (input === null || output === null) return null
    return {input, output}
}

/**
 서버 시작 시각 + 시작 응답을 받은 뒤 로컬에서 잰 경과 = AI 시작·완료 시각(ISO-8601, Z). 못 읽으면 null.
 서버가 마이크로초로 주면 Date 가 밀리초로 잘라 읽으므로 1ms 를 더해 시작보다 앞서지 않게 한다.
 */
export function creditCallTime(startedAt, elapsedMs) {
    const start = typeof startedAt === "string" ? Date.parse(startedAt) : NaN
    if (!Number.isFinite(start)) return null
    const elapsed = Number.isFinite(elapsedMs) && elapsedMs > 0 ? Math.ceil(elapsedMs) : 0
    return new Date(start + elapsed + 1).toISOString()
}

/** AI 를 지금 시작해도 되는지: startedAt − 1초 ≤ callStartedAt < validUntil (상한에는 오차를 더하지 않는다) */
export function isWithinStartWindow({startedAt, validUntil} = {}, callStartedAt) {
    const start = Date.parse(startedAt)
    const until = Date.parse(validUntil)
    const at = Date.parse(callStartedAt)
    if (![start, until, at].every(Number.isFinite)) return false
    return at >= start - 1000 && at < until
}

function toNumber(value) {
    if (value === null || value === undefined || value === "") return null
    const number = Number(value)
    return Number.isFinite(number) ? number : null
}

/*
 잔액 조회 응답 → 화면에서 쓰는 값.
 - availableCredits 는 max(0, 지급량 − 정산 사용량)이라 넘겨 써도 0 이다. 0 에서 넘긴 양을 거꾸로 셈하지 않는다.
 - canStart 는 "잔액이 0 보다 크다"일 뿐 AI 이미지 변환 1회를 할 수 있다는 뜻은 아니다 → 견적(parseCreditEstimate)을 함께 본다.
 - periodLimit 0 은 무제한이 아니라 지급 0 이다.
 */
export function parseCreditBalance(data) {
    if (!data || typeof data !== "object") return null
    const available = toNumber(data.availableCredits)
    return {
        contractVersion: data.contractVersion || null,
        canStart: data.canStart === true,
        available: Math.max(0, available ?? 0),
        limit: toNumber(data.periodLimit),
        resetMode: data.resetMode || null,
        nextResetAt: data.nextResetAt || null,
    }
}

/** 견적 응답 → {canStart, required, estimated, available, reason}. canStart 가 없으면 못 읽은 것(null). */
export function parseCreditEstimate(data) {
    if (!data || typeof data !== "object" || typeof data.canStart !== "boolean") return null
    return {
        canStart: data.canStart,
        required: toNumber(data.requiredCredits),
        estimated: toNumber(data.estimatedCredits),
        available: toNumber(data.availableCredits),
        reason: data.reason || null,
    }
}

/**
 남았지만 이 변환 1회 예상 사용량보다 적은가(견적 canStart false). 다 쓴 경우(잔액 canStart false)와 견적을 모르는
 경우는 false — 다 쓴 것은 따로 알리고, 견적을 모르면 승인 API 가 다시 확인한다.
 */
export function isCreditShort(balance, estimate) {
    return !!balance && balance.canStart !== false && estimate?.canStart === false
}

/*
 화면에는 남은 양(%)을 크게 보인다(사용자 결정 2026-10-21: 배터리처럼 100% 에서 줄어든다). 크레딧 수는 그 아래 작게
 「750 / 1,000 크레딧」으로 함께 보인다(매뉴얼 15 「화면: 실제 잔여/한도·%·현지 초기화 시각」).
 - 남은 양 = availableCredits ÷ periodLimit. 내림한다(실제보다 많이 남은 것처럼 보이지 않게).
 - 0 보다 크고 1 미만이면 0% 가 아니라 "1% 미만"(belowOne). 아직 남았는데 0% 로 보이면 안 된다.
 - 잔액이 없으면(canStart false — 다 썼거나 넘겨 썼다) 0.
 - 한도가 0(지급 없음)이거나 모르면 null.
 */
export function creditRemainingDisplay(balance) {
    if (!balance) return null
    if (!(balance.limit > 0)) return null
    if (balance.canStart === false) return {value: 0, belowOne: false}
    const available = Number.isFinite(balance.available) ? balance.available : 0
    const percent = (available / balance.limit) * 100
    if (percent > 0 && percent < 1) return {value: 1, belowOne: true}
    return {value: Math.max(0, Math.min(100, Math.floor(percent))), belowOne: false}
}

/*
 날짜·남은 시간·숫자 서식에 넘길 언어 목록.
 - 브라우저가 그 언어 서식을 모르면(크롬에는 웨일스어 등이 없다) 브라우저 기본 언어로 떨어져 웨일스어 화면에
   "오전 09:00 (11시간 후)"가 나왔다(2026-10-22 26개 언어 점검) → 영어를 다음 후보로 둔다.
 - 화면 언어 "sh" 는 세르비아어 키릴, "sr" 은 라틴이다. Intl 은 sh 를 라틴으로 읽으므로 문자를 밝혀 준다.
 */
export function intlLocales(locale) {
    const mapped = {sh: "sr-Cyrl", sr: "sr-Latn"}[locale] || locale
    return mapped ? [mapped, "en"] : ["en"]
}

/**
 크레딧 수 → 화면 언어 자릿수 묶음으로 "1,000"·"74". 소수는 버린다(실제보다 많이 남은 것처럼 보이지 않게).
 0 보다 크고 1 미만이면 "0" 이 아니라 "<1". 숫자를 못 읽으면 빈 문자열.
 */
export function formatCreditAmount(value, locale) {
    if (typeof value !== "number" || !Number.isFinite(value)) return ""
    const whole = Math.max(0, Math.floor(value))
    let format
    try {
        // 숫자는 아라비아 숫자로 둔다(날짜·남은 시간과 같다).
        format = new Intl.NumberFormat(intlLocales(locale), {maximumFractionDigits: 0, numberingSystem: "latn"})
    } catch (_) {
        format = new Intl.NumberFormat("en", {maximumFractionDigits: 0})
    }
    if (value > 0 && whole === 0) return `<${format.format(1)}`
    return format.format(whole)
}

/*
 초기화 주기(서버 resetMode) → 안내 문구 키. 비용 기반 계약(COST_1)은 DAILY(UTC 자정)만 돌려주지만, 다른 값이 와도
 문구 없이 「다음 초기화」 줄만 보이도록 옛 키는 남겨 둔다.
 "매주 월요일"·"매월 1일"처럼 요일·날짜는 시간대에 따라 달라 보이므로 주기만 말하고, 정확한 시각은 "다음 초기화"
 (보는 사람의 시간대 + 남은 시간)가 맡는다. 한국에서는 UTC 자정이 오전 9시지만 고정 문구로 쓰지 않는다.
 */
const RESET_MODE_KEYS = {
    DAILY: "매일 다시 채워집니다.",
    WEEKLY: "매주 다시 채워집니다.",
    MONTHLY: "매달 다시 채워집니다.",
    NONE: "자동으로 다시 채워지지 않습니다.",
}

export function creditResetModeKey(resetMode) {
    return RESET_MODE_KEYS[resetMode] || ""
}

/**
 헤더 표시 단계: 'empty'(다 씀) · 'short'(남았지만 AI 이미지 변환 1회에 모자람) · 'warn'(20% 이하) · 'ok' · 'unknown'(잔액 모름)
 short 는 견적으로 판단한 값을 넘겨받는다(isCreditShort).
 */
export function creditRemainingLevel(balance, short = false) {
    if (!balance) return "unknown"
    if (balance.canStart === false) return "empty"
    if (short) return "short"
    const display = creditRemainingDisplay(balance)
    if (!display) return "unknown"
    return display.value <= 20 ? "warn" : "ok"
}

/*
 다음 초기화까지 남은 시간 → 화면 언어로 "17시간 후" · "in 17 hours" · "내일".
 초기화는 UTC 자정이라 나라마다 현지 시각이 다르다. 절대 시각만 보이면 시간대를 헷갈릴 수 있으니 어디서 봐도 같은
 "남은 시간"을 함께 보인다. 지났거나 못 읽으면 빈 문자열.
 */
export function formatCreditResetRelative(iso, locale, nowMs = Date.now()) {
    const at = iso ? Date.parse(iso) : NaN
    if (!Number.isFinite(at)) return ""
    const diff = at - nowMs
    if (diff <= 0) return ""
    let value
    let unit
    if (diff < 60 * 60 * 1000) {
        value = Math.max(1, Math.ceil(diff / 60000))
        unit = "minute"
    } else if (diff < 48 * 60 * 60 * 1000) {
        value = Math.round(diff / 3600000)
        unit = "hour"
    } else {
        value = Math.round(diff / 86400000)
        unit = "day"
    }
    try {
        // 숫자는 아라비아 숫자로 둔다(마라티어는 Intl 기본 숫자가 데바나가리라 "(९ तासांमध्ये)" 였다).
        return new Intl.RelativeTimeFormat(intlLocales(locale), {numeric: "auto", numberingSystem: "latn"}).format(value, unit)
    } catch (_) {
        return new Intl.RelativeTimeFormat("en", {numeric: "auto"}).format(value, unit)
    }
}

/** 초기화 시각을 "2026. 10. 22. 오전 09:00 (17시간 후)" 로. 남은 시간을 모르면 시각만. */
export function formatCreditResetWithRelative(iso, locale, nowMs = Date.now()) {
    const at = formatCreditResetAt(iso, locale)
    if (!at) return ""
    const relative = formatCreditResetRelative(iso, locale, nowMs)
    // "(11일 후)" 도 한 덩어리로(괄호 안에서 줄이 갈리지 않게)
    return relative ? `${at} (${relative.replace(/ /g, " ")})` : at
}

/** 초기화 시각(ISO) → 화면 언어·보는 사람의 시간대로 "2026. 10. 22. 오전 09:00". 못 읽으면 빈 문자열. */
export function formatCreditResetAt(iso, locale) {
    const date = iso ? new Date(iso) : null
    if (!date || Number.isNaN(date.getTime())) return ""
    // 숫자는 아라비아 숫자로 둔다(마라티어는 Intl 기본 숫자가 데바나가리라 "२७/१०/२०२६, ०९:००" 였다).
    const style = {year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", numberingSystem: "latn"}
    let text
    try {
        text = date.toLocaleString(intlLocales(locale), style)
    } catch (_) {
        text = date.toLocaleString("en", style)
    }
    // 문장 안에서 "2026. 10. / 22." 처럼 날짜 중간에서 줄이 바뀌지 않게 띄어쓰기를 줄바꿈 없는 공백으로
    return text.replace(/ /g, " ")
}

export function creditErrorCode(data) {
    return Array.isArray(data?.errors) && data.errors[0]?.code ? String(data.errors[0].code) : null
}

/**
 같은 값으로 다시 보내 볼 만한 응답인지. 응답을 못 받았거나(0) 서버·프록시 오류일 때만.
 503 은 업무 코드가 없을 때(프록시가 잠깐 못 넘김)만 — CREDIT_POLICY_NOT_CONFIGURED 같은 설정 오류는 다시 보내도 같다.
 */
export function isRetryableCreditResponse(response) {
    const status = Number(response?.status) || 0
    if (status === 503) return !creditErrorCode(response?.data)
    return status === 0 || status === 500 || status === 502 || status === 504
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

async function sendWithRetry(send, {delays = RETRY_DELAYS, wait = sleep} = {}) {
    let response = await send()
    for (const delay of delays) {
        if (!isRetryableCreditResponse(response)) break
        await wait(delay)
        response = await send()
    }
    return response
}

function defaultStorage() {
    try {
        return globalThis.localStorage || null
    } catch (_) {
        return null
    }
}

function readPending(storage) {
    try {
        const list = JSON.parse(storage?.getItem(PENDING_KEY) || "[]")
        return Array.isArray(list) ? list : []
    } catch (_) {
        return []
    }
}

function writePending(storage, list) {
    try {
        if (!storage) return
        if (list.length) storage.setItem(PENDING_KEY, JSON.stringify(list.slice(-MAX_PENDING)))
        else storage.removeItem(PENDING_KEY)
    } catch (_) {
        // 사생활 보호 창·저장소 차단: 다음 기회가 없을 뿐 지금 작업은 계속한다.
    }
}

function dropLegacyPending(storage) {
    try {
        storage?.removeItem(LEGACY_PENDING_KEY)
    } catch (_) {
        // 위와 같다
    }
}

function sendSettlement(api, item) {
    return item.action === "complete"
        ? api.completeCall(item.userNo, item.callId, item.body)
        : api.markCallUnknown(item.userNo, item.callId)
}

/**
 호출 하나를 마무리한다. completion 이 있으면 실제 사용량 정산, 없으면 사용량 미확인 기록.
 끝내 응답을 못 받으면 저장해 두고 다음에 열 때 같은 값으로 보낸다.
 정산 본문이 거절되면(시각·사용량 형식 등) 시작만 된 채로 두지 않고 미확인으로라도 남긴다.
 */
export async function settleCreditCall({api, userNo, callId, completion = null, storage = defaultStorage(), wait} = {}) {
    const item = completion
        ? {userNo, callId, action: "complete", body: completion}
        : {userNo, callId, action: "unknown"}
    const response = await sendWithRetry(() => sendSettlement(api, item), {wait})
    if (isRetryableCreditResponse(response)) {
        writePending(storage, [...readPending(storage), item])
        return response
    }
    if (completion && Number(response?.status) !== 200) {
        console.error("[aiCredit] 정산 거절:", response?.status, creditErrorCode(response?.data))
        await sendWithRetry(() => api.markCallUnknown(userNo, callId), {wait})
    }
    return response
}

/** 지난번에 못 보낸 이 사용자의 정산·미확인 기록을 보낸다. 다른 계정 것은 그 계정이 열 때까지 둔다. */
export async function flushPendingCreditCalls({api, userNo, storage = defaultStorage()} = {}) {
    dropLegacyPending(storage)
    const pending = readPending(storage)
    if (!pending.length) return 0
    const keep = []
    let sent = 0
    for (const item of pending) {
        // 깨진 항목은 버린다
        if (!item?.callId || (item.action !== "unknown" && !(item.action === "complete" && item.body))) continue
        if (item.userNo !== userNo) {
            keep.push(item)
            continue
        }
        const response = await sendSettlement(api, item)
        if (isRetryableCreditResponse(response)) keep.push(item)
        else sent += 1
    }
    writePending(storage, keep)
    return sent
}

const defaultClock = () => (typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now())

function abortError() {
    // axios 의 Cancel 처럼 code 를 단다. 화면은 요청 번호로 멈춘 것을 가리므로 오류 모양에 기대지 않는다.
    return Object.assign(new Error("canceled"), {name: "AbortError", code: "ERR_CANCELED"})
}

async function closeQuietly(api, userNo, requestId, wait) {
    if (!requestId) return
    try {
        await sendWithRetry(() => api.closeRequest(userNo, requestId), {wait})
    } catch (_) {
        // 닫지 못해도 남는 것은 차감 없는 PLANNED 슬롯뿐이다.
    }
}

/** 요청 승인 → {requestId, callId}. 승인된 호출은 배열 위치가 아니라 callKey·operation 으로 찾는다. */
async function approveCreditRequest({api, userNo, operation, requestKey, wait}) {
    const response = await sendWithRetry(() => api.approveRequest(userNo, creditPlan(operation, requestKey)), {wait})
    const status = Number(response?.status) || 0
    if (status !== 200 && status !== 201) {
        throw new CreditError(creditErrorCode(response?.data) || "CREDIT_REQUEST_FAILED", {status})
    }
    const data = response.data || {}
    const calls = Array.isArray(data.calls) ? data.calls : []
    const call = calls.find(item => item?.callKey === `${operation}:0`) || calls.find(item => item?.operation === operation)
    if (!data.requestId || !call?.callId) throw new CreditError("CREDIT_REQUEST_FAILED", {status})
    // 새 키라 이미 닫혔거나 쓴 호출이 올 수 없다. 오면 AI 를 돌리지 않는다.
    if (data.status !== "APPROVED" || call.status !== "PLANNED") {
        await closeQuietly(api, userNo, data.requestId, wait)
        throw new CreditError("CREDIT_REQUEST_FAILED", {status})
    }
    return {requestId: data.requestId, callId: call.callId}
}

/**
 크레딧 요청 하나로 AI 를 한 번 돌린다.
 - run: AI 를 부르고 응답 본문(tokens 포함)을 돌려주는 함수
 - operation·model: AI_IMAGE_OPERATIONS 의 값(이미지 → dotai.image2dots, 설명 글 → dotai.text2dots)
 - signal: 승인·시작 사이에 「취소」하면 AI 를 부르지 않고 요청을 닫는다
 - returns: {result, call: {requestId, callId, startedAt}, usage, settled}
 - throws: 승인·시작 실패는 CreditError(code = 서버 errors[].code), AI 실패는 run 이 던진 오류 그대로(정산·미확인 기록 뒤)

 화면이 그사이 닫히거나 다른 단계로 가도 정산은 여기서 끝낸다. 토큰은 이미 썼기 때문이다.
 */
export async function runCreditedCall({
    api,
    userNo,
    run,
    operation = AI_IMAGE_OPERATIONS.image.operation,
    model = AI_IMAGE_OPERATIONS.image.model,
    requestKey,
    signal,
    clock = defaultClock,
    storage = defaultStorage(),
    wait,
} = {}) {
    if (!isCreditUserNo(userNo)) throw new CreditError("CREDIT_USER_UNAVAILABLE")
    if (signal?.aborted) throw abortError()

    // 1) 요청 승인 → 2) 호출 시작 승인. 그사이 단가·환산 설정이 바뀌면(PRICE_CHANGED) 이전 계획을 닫고 새 키로 한 번만 다시 승인한다.
    let approval
    let start
    for (let attempt = 0; ; attempt += 1) {
        approval = await approveCreditRequest({
            api,
            userNo,
            operation,
            requestKey: attempt === 0 && requestKey ? requestKey : createCreditRequestKey(operation),
            wait,
        })
        if (signal?.aborted) {
            await closeQuietly(api, userNo, approval.requestId, wait)
            throw abortError()
        }
        start = await sendWithRetry(() => api.startCall(userNo, approval.callId, {model}), {wait})
        if (Number(start?.status) === 200) break
        const code = creditErrorCode(start?.data)
        await closeQuietly(api, userNo, approval.requestId, wait)
        if (code === "PRICE_CHANGED" && attempt === 0) continue
        throw new CreditError(code || "CREDIT_START_FAILED", {status: Number(start?.status) || 0})
    }
    const base = clock()
    const startData = start.data || {}
    // replayed=true + STARTED 는 첫 시작 응답을 못 받아 같은 callId 로 다시 보낸 경우뿐이다(callId 는 방금 새 키로 받았다)
    // → AI 는 아직 한 번도 돌지 않았다. 정산·미확인까지 끝난 호출이 오면 AI 를 다시 돌리지 않는다.
    if (startData.status !== "STARTED") {
        throw new CreditError("CREDIT_CALL_ALREADY_USED", {status: 200})
    }
    const callStartedAt = creditCallTime(startData.startedAt, clock() - base)
    if (!callStartedAt || !isWithinStartWindow(startData, callStartedAt)) {
        // 승인된 시작 창(기본 30초)을 넘겼으면 AI 를 새로 시작하지 않는다. 시작 상태를 되돌리는 API 는 없다.
        throw new CreditError("CREDIT_START_WINDOW_EXPIRED", {status: 200})
    }

    let result
    let failure = null
    try {
        result = await run()
    } catch (error) {
        failure = error
    }
    const completedAt = creditCallTime(startData.startedAt, clock() - base)
    // 실패 응답에 사용량이 실려 오면 그것도 정산한다. 못 읽으면 미확인.
    const usage = usageFromTokens(failure ? failure?.response?.data?.tokens : result?.tokens)
    const completion = usage ? {contractVersion: CREDIT_CONTRACT, callStartedAt, completedAt, usage} : null
    const settled = await settleCreditCall({api, userNo, callId: approval.callId, completion, storage, wait})
    if (failure) throw failure
    return {result, call: {...approval, startedAt: startData.startedAt}, usage, settled}
}
